/**
 * FieldX Waves - Interactive 3D Wave Pattern Library
 * https://github.com/fieldx/fieldx-waves
 *
 * Usage:
 *   <script src="https://cdn.jsdelivr.net/npm/fieldx-waves"></script>
 *   <script>
 *     FieldXWaves.create('#container', config);
 *   </script>
 */

import * as THREE from "three";

// ============================================
// TYPES
// ============================================

export type WaveFuncType =
	| "sine"
	| "cosine"
	| "sawtooth"
	| "square"
	| "triangle"
	| "noise";
export type BaseType =
	| "horizontal"
	| "vertical"
	| "grid"
	| "radial"
	| "circular"
	| "field";
export type AxisType = "x" | "y" | "z" | "xy" | "radial";

export interface WaveFunction {
	id: string;
	type: WaveFuncType;
	axis: AxisType;
	frequency: number;
	amplitude: number;
	phase: number;
	speed: number;
}

export interface WaveLayer {
	id: string;
	enabled: boolean;
	baseType: BaseType;
	params: {
		lineCount: number;
		pointsPerLine: number;
		spacing: number;
		spread: number;
		dotSize: number;
		color: string;
		opacity: number;
		xOffset: number;
		yOffset: number;
		zOffset: number;
		rotation: number;
	};
	waveFunctions: WaveFunction[];
}

export interface BackgroundParams {
	starCount: number;
	starColor: string;
	starSize: number;
	bgColor: string;
	starOpacity: number;
}

export interface FieldXWavesConfig {
	layers: WaveLayer[];
	background: BackgroundParams;
	camera?: {
		position?: [number, number, number];
		fov?: number;
	};
	autoplay?: boolean;
	quality?: "low" | "medium" | "high";
}

export interface FieldXWavesOptions {
	config: FieldXWavesConfig;
	responsive?: boolean;
	pauseOnHidden?: boolean;
}

// ============================================
// WAVE FUNCTION EVALUATOR
// ============================================

function evaluateWaveFunc(
	wf: WaveFunction,
	x: number,
	y: number,
	t: number,
): { dx: number; dy: number; dz: number } {
	const phase = t * wf.speed + wf.phase;
	const input =
		(wf.axis === "radial"
			? Math.sqrt(x * x + y * y)
			: wf.axis === "xy"
				? x + y
				: wf.axis === "x"
					? x
					: y) *
			wf.frequency +
		phase;

	let result = 0;
	switch (wf.type) {
		case "sine":
			result = Math.sin(input);
			break;
		case "cosine":
			result = Math.cos(input);
			break;
		case "sawtooth":
			result = 2 * (input / Math.PI - Math.floor(input / Math.PI + 0.5));
			break;
		case "square":
			result = Math.sin(input) > 0 ? 1 : -1;
			break;
		case "triangle":
			result = (2 * Math.asin(Math.sin(input))) / Math.PI;
			break;
		case "noise":
			result = Math.sin(input) * Math.cos(input * 1.5) * Math.sin(input * 0.5);
			break;
	}

	result *= wf.amplitude;

	let dx = 0,
		dy = 0,
		dz = 0;
	switch (wf.axis) {
		case "x":
			dx = result;
			break;
		case "y":
			dy = result;
			break;
		case "z":
			dz = result;
			break;
		case "xy":
			dx = result * 0.5;
			dy = result * 0.5;
			break;
		case "radial":
			const angle = Math.atan2(y, x);
			dx = Math.cos(angle) * result * 0.3;
			dy = Math.sin(angle) * result * 0.3;
			dz = result * 0.5;
			break;
	}
	return { dx, dy, dz };
}

// ============================================
// QUALITY MULTIPLIERS
// ============================================

const QUALITY_MULTIPLIERS = {
	low: 0.5,
	medium: 1,
	high: 1.5,
};

// ============================================
// MAIN CLASS
// ============================================

export class FieldXWavesRenderer {
	private container: HTMLElement;
	private config: FieldXWavesConfig;
	private options: FieldXWavesOptions;

	private scene: THREE.Scene;
	private camera: THREE.PerspectiveCamera;
	private renderer: THREE.WebGLRenderer;

	private layerMeshes: Map<
		string,
		{ mesh: THREE.Points; basePositions: Float32Array }
	> = new Map();
	private starsMesh: THREE.Points | null = null;

	private time: number = 0;
	private isPlaying: boolean = false;
	private animationId: number | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private visibilityHandler: (() => void) | null = null;

	constructor(container: HTMLElement | string, options: FieldXWavesOptions) {
		// Resolve container
		if (typeof container === "string") {
			const el = document.querySelector(container);
			if (!el)
				throw new Error(`FieldXWaves: Container "${container}" not found`);
			this.container = el as HTMLElement;
		} else {
			this.container = container;
		}

		this.options = {
			responsive: true,
			pauseOnHidden: true,
			...options,
		};
		this.config = options.config;

		// Initialize Three.js
		this.scene = new THREE.Scene();

		const cameraPos = this.config.camera?.position || [0, 0, 20];
		const cameraFov = this.config.camera?.fov || 50;
		this.camera = new THREE.PerspectiveCamera(
			cameraFov,
			this.getAspect(),
			0.1,
			1000,
		);
		this.camera.position.set(...cameraPos);

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(
			this.container.clientWidth,
			this.container.clientHeight,
		);
		this.container.appendChild(this.renderer.domElement);

		// Set background color
		this.scene.background = new THREE.Color(this.config.background.bgColor);

		// Build scene
		this.buildScene();

		// Setup responsive handling
		if (this.options.responsive) {
			this.setupResizeObserver();
		}

		// Setup visibility handling
		if (this.options.pauseOnHidden) {
			this.setupVisibilityHandler();
		}

		// Start animation
		if (this.config.autoplay !== false) {
			this.play();
		}
	}

	private getAspect(): number {
		return this.container.clientWidth / this.container.clientHeight;
	}

	private getQualityMultiplier(): number {
		return QUALITY_MULTIPLIERS[this.config.quality || "medium"];
	}

	private buildScene(): void {
		// Clear existing
		this.layerMeshes.forEach(({ mesh }) => this.scene.remove(mesh));
		this.layerMeshes.clear();
		if (this.starsMesh) {
			this.scene.remove(this.starsMesh);
			this.starsMesh = null;
		}

		// Build background stars
		this.buildStars();

		// Build wave layers
		this.config.layers.forEach((layer) => {
			if (layer.enabled) {
				this.buildLayer(layer);
			}
		});
	}

	private buildStars(): void {
		const bg = this.config.background;
		if (bg.starCount <= 0) return;

		const starCount = Math.floor(bg.starCount * this.getQualityMultiplier());
		const positions = new Float32Array(starCount * 3);
		const starColor = new THREE.Color(bg.starColor);

		for (let i = 0; i < starCount; i++) {
			positions[i * 3] = (Math.random() - 0.5) * 100;
			positions[i * 3 + 1] = (Math.random() - 0.5) * 80;
			positions[i * 3 + 2] = (Math.random() - 0.5) * 50 - 10;
		}

		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

		const material = new THREE.PointsMaterial({
			size: bg.starSize,
			color: starColor,
			transparent: true,
			opacity: bg.starOpacity,
			sizeAttenuation: true,
		});

		this.starsMesh = new THREE.Points(geometry, material);
		this.scene.add(this.starsMesh);
	}

	private buildLayer(layer: WaveLayer): void {
		const { baseType, params } = layer;
		const qualityMult = this.getQualityMultiplier();

		const lineCount = Math.floor(params.lineCount * qualityMult);
		const pointsPerLine = Math.floor(params.pointsPerLine * qualityMult);
		const totalPoints = lineCount * pointsPerLine;

		const positions = new Float32Array(totalPoints * 3);
		const colors = new Float32Array(totalPoints * 3);
		const baseColor = new THREE.Color(params.color);

		let idx = 0;
		for (let i = 0; i < lineCount; i++) {
			const lineProgress = i / Math.max(1, lineCount - 1);
			const lineColor = baseColor.clone();
			lineColor.offsetHSL(0, 0, (lineProgress - 0.5) * 0.08);

			for (let j = 0; j < pointsPerLine; j++) {
				const pointProgress = j / Math.max(1, pointsPerLine - 1);
				let x = 0,
					y = 0,
					z = 0;

				switch (baseType) {
					case "horizontal":
						x = (pointProgress - 0.5) * 50 * params.spread;
						y = (lineProgress - 0.5) * lineCount * params.spacing;
						break;
					case "vertical":
						x = (lineProgress - 0.5) * lineCount * params.spacing;
						y = (pointProgress - 0.5) * 40 * params.spread;
						break;
					case "grid":
						const gridCols = Math.ceil(Math.sqrt(lineCount));
						const gridX = (i % gridCols) / Math.max(1, gridCols - 1);
						const gridY =
							Math.floor(i / gridCols) /
							Math.max(1, Math.ceil(lineCount / gridCols) - 1);
						x = (gridX - 0.5) * 30 * params.spread;
						y = (gridY - 0.5) * 25 * params.spread;
						break;
					case "radial":
						const radialAngle = pointProgress * Math.PI * 2;
						const radialRadius = 2 + lineProgress * 18 * params.spread;
						x = Math.cos(radialAngle) * radialRadius;
						y = Math.sin(radialAngle) * radialRadius;
						break;
					case "circular":
						const circleAngle = lineProgress * Math.PI * 2;
						const circleRadius = 3 + pointProgress * 15 * params.spread;
						x = Math.cos(circleAngle) * circleRadius;
						y = Math.sin(circleAngle) * circleRadius;
						break;
					case "field":
						const seed = i * 123.45 + j * 67.89;
						x = ((seed % 100) / 100 - 0.5) * 45 * params.spread;
						y =
							(((Math.sin(seed) * 100) % 100) / 100 - 0.5) * 35 * params.spread;
						z = (((Math.cos(seed) * 100) % 100) / 100 - 0.5) * 3;
						break;
				}

				positions[idx * 3] = x;
				positions[idx * 3 + 1] = y;
				positions[idx * 3 + 2] = z;
				colors[idx * 3] = lineColor.r;
				colors[idx * 3 + 1] = lineColor.g;
				colors[idx * 3 + 2] = lineColor.b;
				idx++;
			}
		}

		const basePositions = new Float32Array(positions);

		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

		const material = new THREE.PointsMaterial({
			size: params.dotSize,
			vertexColors: true,
			transparent: true,
			opacity: params.opacity,
			sizeAttenuation: true,
			blending: THREE.AdditiveBlending,
		});

		const mesh = new THREE.Points(geometry, material);
		this.scene.add(mesh);

		this.layerMeshes.set(layer.id, { mesh, basePositions });
	}

	private updateLayers(): void {
		this.config.layers.forEach((layer) => {
			if (!layer.enabled) return;

			const layerData = this.layerMeshes.get(layer.id);
			if (!layerData) return;

			const { mesh, basePositions } = layerData;
			const { params, waveFunctions } = layer;
			const posArray = mesh.geometry.attributes.position.array as Float32Array;

			const qualityMult = this.getQualityMultiplier();
			const lineCount = Math.floor(params.lineCount * qualityMult);
			const pointsPerLine = Math.floor(params.pointsPerLine * qualityMult);

			let idx = 0;
			for (let i = 0; i < lineCount; i++) {
				for (let j = 0; j < pointsPerLine; j++) {
					let baseX = basePositions[idx * 3];
					let baseY = basePositions[idx * 3 + 1];
					let baseZ = basePositions[idx * 3 + 2];

					if (params.rotation !== 0) {
						const cosR = Math.cos(params.rotation);
						const sinR = Math.sin(params.rotation);
						const rx = baseX * cosR - baseY * sinR;
						const ry = baseX * sinR + baseY * cosR;
						baseX = rx;
						baseY = ry;
					}

					let totalDx = 0,
						totalDy = 0,
						totalDz = 0;
					for (const wf of waveFunctions) {
						const { dx, dy, dz } = evaluateWaveFunc(
							wf,
							baseX,
							baseY,
							this.time,
						);
						totalDx += dx;
						totalDy += dy;
						totalDz += dz;
					}

					posArray[idx * 3] = baseX + totalDx + params.xOffset;
					posArray[idx * 3 + 1] = baseY + totalDy + params.yOffset;
					posArray[idx * 3 + 2] = baseZ + totalDz + params.zOffset;
					idx++;
				}
			}
			mesh.geometry.attributes.position.needsUpdate = true;
		});
	}

	private animate = (): void => {
		if (!this.isPlaying) return;

		this.animationId = requestAnimationFrame(this.animate);
		this.time += 0.016; // ~60fps
		this.updateLayers();
		this.renderer.render(this.scene, this.camera);
	};

	private setupResizeObserver(): void {
		this.resizeObserver = new ResizeObserver(() => {
			const width = this.container.clientWidth;
			const height = this.container.clientHeight;

			this.camera.aspect = width / height;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(width, height);
		});
		this.resizeObserver.observe(this.container);
	}

	private setupVisibilityHandler(): void {
		this.visibilityHandler = () => {
			if (document.hidden) {
				this.pause();
			} else {
				this.play();
			}
		};
		document.addEventListener("visibilitychange", this.visibilityHandler);
	}

	// ============================================
	// PUBLIC API
	// ============================================

	/**
	 * Start/resume animation
	 */
	play(): void {
		if (this.isPlaying) return;
		this.isPlaying = true;
		this.animate();
	}

	/**
	 * Pause animation
	 */
	pause(): void {
		this.isPlaying = false;
		if (this.animationId) {
			cancelAnimationFrame(this.animationId);
			this.animationId = null;
		}
	}

	/**
	 * Toggle play/pause
	 */
	toggle(): void {
		if (this.isPlaying) {
			this.pause();
		} else {
			this.play();
		}
	}

	/**
	 * Check if currently playing
	 */
	isAnimating(): boolean {
		return this.isPlaying;
	}

	/**
	 * Update configuration and rebuild scene
	 */
	setConfig(config: FieldXWavesConfig): void {
		this.config = config;
		this.scene.background = new THREE.Color(config.background.bgColor);
		this.buildScene();
	}

	/**
	 * Update a specific layer
	 */
	updateLayer(layerId: string, updates: Partial<WaveLayer>): void {
		const layer = this.config.layers.find((l) => l.id === layerId);
		if (layer) {
			Object.assign(layer, updates);
			this.buildScene();
		}
	}

	/**
	 * Set quality level
	 */
	setQuality(quality: "low" | "medium" | "high"): void {
		this.config.quality = quality;
		this.buildScene();
	}

	/**
	 * Get current configuration (for export)
	 */
	getConfig(): FieldXWavesConfig {
		return JSON.parse(JSON.stringify(this.config));
	}

	/**
	 * Destroy instance and cleanup
	 */
	destroy(): void {
		this.pause();

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}

		if (this.visibilityHandler) {
			document.removeEventListener("visibilitychange", this.visibilityHandler);
		}

		this.layerMeshes.forEach(({ mesh }) => {
			mesh.geometry.dispose();
			(mesh.material as THREE.Material).dispose();
		});

		if (this.starsMesh) {
			this.starsMesh.geometry.dispose();
			(this.starsMesh.material as THREE.Material).dispose();
		}

		this.renderer.dispose();
		this.container.removeChild(this.renderer.domElement);
	}
}

// ============================================
// FACTORY FUNCTION
// ============================================

/**
 * Create a new FieldXWaves instance
 *
 * @example
 * const waves = FieldXWaves.create('#container', {
 *   config: { layers: [...], background: {...} }
 * });
 *
 * // Or with just config
 * const waves = FieldXWaves.create('#container', myExportedConfig);
 */
export function create(
	container: HTMLElement | string,
	optionsOrConfig: FieldXWavesOptions | FieldXWavesConfig,
): FieldXWavesRenderer {
	// Allow passing config directly without wrapping in options
	const options: FieldXWavesOptions =
		"layers" in optionsOrConfig
			? { config: optionsOrConfig as FieldXWavesConfig }
			: (optionsOrConfig as FieldXWavesOptions);

	return new FieldXWavesRenderer(container, options);
}

// ============================================
// AUTO-INIT FROM DATA ATTRIBUTES
// ============================================

export function autoInit(): void {
	const elements = document.querySelectorAll("[data-fieldx-waves]");

	elements.forEach((el) => {
		const configAttr = el.getAttribute("data-fieldx-waves");
		if (!configAttr) return;

		try {
			const config = JSON.parse(configAttr) as FieldXWavesConfig;
			create(el as HTMLElement, config);
		} catch (e) {
			console.error("FieldXWaves: Invalid config JSON", e);
		}
	});
}

// Auto-init on DOM ready
if (typeof document !== "undefined") {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", autoInit);
	} else {
		// DOM already ready, init on next tick to allow script to finish
		setTimeout(autoInit, 0);
	}
}

// ============================================
// EXPORTS
// ============================================

export const FieldXWaves = {
	create,
	autoInit,
	Renderer: FieldXWavesRenderer,
};

export default FieldXWaves;
